import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "./use-auth";

// Hook para buscar planos pendentes de aprovação
export function usePendingPlans(companyId?: string) {
  return useQuery({
    queryKey: ["pendingPlans", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .select(`
          *,
          fleet_vehicles(id, plate, make, model, odometer)
        `)
        .eq("company_id", companyId)
        .eq("approval_status", "pending")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // Buscar informações do criador separadamente
      if (data) {
        const creatorIds = data.map(p => p.created_by).filter(Boolean);
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", creatorIds);
        
        return data.map(plan => ({
          ...plan,
          created_by_profile: profiles?.find(p => p.id === plan.created_by)
        }));
      }
      
      return data || [];
    },
    enabled: !!companyId,
  });
}

// Hook para aprovar plano de manutenção
export function useApprovePlan(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (planId: string) => {
      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .update({
          approval_status: 'approved',
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
          status: 'pending'
        })
        .eq("id", planId)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      queryClient.invalidateQueries({ queryKey: ["pendingPlans", companyId] });
      toast({
        title: "Plano Aprovado",
        description: "O plano de manutenção foi aprovado com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error approving plan:", error);
      toast({
        title: "Erro",
        description: "Erro ao aprovar plano de manutenção.",
        variant: "destructive",
      });
    },
  });
}

// Hook para rejeitar plano de manutenção
export function useRejectPlan(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ planId, reason }: { planId: string; reason: string }) => {
      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .update({
          approval_status: 'rejected',
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
          rejection_reason: reason,
          status: 'inactive'
        })
        .eq("id", planId)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      queryClient.invalidateQueries({ queryKey: ["pendingPlans", companyId] });
      toast({
        title: "Plano Rejeitado",
        description: "O plano de manutenção foi rejeitado.",
        variant: "destructive",
      });
    },
    onError: (error) => {
      console.error("Error rejecting plan:", error);
      toast({
        title: "Erro",
        description: "Erro ao rejeitar plano de manutenção.",
        variant: "destructive",
      });
    },
  });
}
